import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { InfoSection } from '@/components/onboarding/sections/InfoSection';
import { KbSection } from '@/components/onboarding/sections/KbSection';
import { MetaOauthSection } from '@/components/onboarding/sections/MetaOauthSection';
import { PreviewSection } from '@/components/onboarding/sections/PreviewSection';
import { StripeSection } from '@/components/onboarding/sections/StripeSection';
import { useOnboardingState } from '@/contexts/OnboardingContext';
import { cn } from '@/lib/utils';
import type { OnboardingState, OnboardingStep } from '@/services/onboardingService';
import type { TenantProfile } from '@/services/tenantProfileService';

export type SectionKey = Exclude<OnboardingStep, 'completed'>;
type CompletedColumn =
  | 'info_completed_at'
  | 'kb_completed_at'
  | 'preview_completed_at'
  | 'meta_oauth_completed_at'
  | 'stripe_completed_at';

/** F063 — props threaded into a section opened as a local, non-destructive
 * edit of an already-completed step. ``editing`` sections must never mutate
 * ``current_step`` (no ``markStepCompleted`` advance, no draft writes);
 * ``onEditDone`` collapses the node after a successful save. */
export interface SectionEditProps {
  editing?: boolean;
  onEditDone?: () => void;
}

interface StepDef {
  key: SectionKey;
  labelKey: string;
  completedColumn: CompletedColumn;
  render: (edit: SectionEditProps) => ReactNode;
}

const STEPS: readonly StepDef[] = [
  {
    key: 'info',
    labelKey: 'onboarding.wizard.steps.info',
    completedColumn: 'info_completed_at',
    render: (edit) => <InfoSection {...edit} />,
  },
  {
    key: 'kb',
    labelKey: 'onboarding.wizard.steps.kb',
    completedColumn: 'kb_completed_at',
    render: (edit) => <KbSection {...edit} />,
  },
  {
    key: 'preview',
    labelKey: 'onboarding.wizard.steps.preview',
    completedColumn: 'preview_completed_at',
    render: (edit) => <PreviewSection {...edit} />,
  },
  {
    key: 'meta_oauth',
    labelKey: 'onboarding.wizard.steps.meta_oauth',
    completedColumn: 'meta_oauth_completed_at',
    render: () => <MetaOauthSection />,
  },
  {
    key: 'stripe_checkout',
    labelKey: 'onboarding.wizard.steps.stripe_checkout',
    completedColumn: 'stripe_completed_at',
    render: () => <StripeSection />,
  },
] as const;

type StepUiState = 'completed' | 'current' | 'locked' | 'stale';

function stepUiState(
  step: StepDef,
  state: OnboardingState | null,
  profile: TenantProfile | null
): StepUiState {
  if (!state) return 'locked';
  if (state.current_step === 'completed') {
    return 'completed';
  }
  // `current` MUST win over `completed` so the boundary step expands even
  // when its completion timestamp is already set (e.g. the pointer parked on
  // a re-completed step). Otherwise the timestamp shadows the pointer.
  if (state.current_step === step.key) {
    return 'current';
  }
  const completedAt = state[step.completedColumn];
  if (step.key === 'kb' && completedAt && profile && profile.knowledge_md === '') {
    return 'stale';
  }
  if (completedAt) return 'completed';
  return 'locked';
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

interface OnboardingTimelineProps {
  /** Deep-link entry (e.g. the status chip's "Reconectar" CTA): open this
   * step as a local edit on arrival. Honored only when the funnel is
   * completed — mid-funnel the boundary step keeps priority, so a deep-link
   * can never expand a locked node. */
  initialEditStep?: SectionKey | null;
  /** Pass `location.key` so a repeated deep-link (same step, new navigation)
   * re-expands the node even after the owner collapsed it. */
  editRequestId?: string;
}

export function OnboardingTimeline({
  initialEditStep = null,
  editRequestId,
}: OnboardingTimelineProps = {}) {
  const { t } = useTranslation();
  const { state, profile, metaOauthOutcome } = useOnboardingState();
  const expandedRef = useRef<HTMLLIElement | null>(null);

  // F063 — which step the owner opened for editing. Pure presentation state:
  // entering/leaving edit mode never touches the persisted `current_step`
  // (that pointer feeds sidebar/chip/CTA/login-redirect app-wide).
  const [editingStep, setEditingStep] = useState<SectionKey | null>(null);

  const funnelCompleted = state?.current_step === 'completed';
  useEffect(() => {
    if (initialEditStep && funnelCompleted) {
      setEditingStep(initialEditStep);
    }
  }, [initialEditStep, funnelCompleted, editRequestId]);

  const boundaryKey: SectionKey | null =
    state && state.current_step !== 'completed' ? state.current_step : null;
  // Returning from the Meta consent screen reloads the SPA (editingStep is
  // lost), so a re-connect from a completed funnel expands the OAuth node
  // while its outcome card is pending — otherwise the result is invisible.
  const expandedKey: SectionKey | null =
    editingStep ?? boundaryKey ?? (metaOauthOutcome.kind !== 'idle' ? 'meta_oauth' : null);

  useEffect(() => {
    const node = expandedRef.current;
    if (!node) return;
    node.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'start',
    });
    // Focus the first focusable element inside the expanded body so keyboard
    // users land in the active form without re-tabbing.
    const focusable = node.querySelector<HTMLElement>(
      'input:not([disabled]), textarea:not([disabled]), select:not([disabled]), button:not([disabled])'
    );
    if (focusable) {
      focusable.focus({ preventScroll: true });
    }
  }, [expandedKey]);

  function handleEdit(key: SectionKey) {
    setEditingStep(key);
  }

  return (
    <ol
      aria-label={t('bot.timeline.label')}
      className="flex flex-col"
      data-testid="onboarding-timeline"
    >
      {STEPS.map((step, idx) => {
        const ui = stepUiState(step, state, profile);
        const isExpanded = step.key === expandedKey;
        const isEditing = step.key === editingStep;
        const isLast = idx === STEPS.length - 1;
        const summary = isExpanded
          ? null
          : ui === 'completed'
            ? buildSummary(step.key, profile)
            : null;
        const nodeProps = isExpanded ? { ref: expandedRef, 'aria-current': 'step' as const } : {};

        return (
          <li
            key={step.key}
            data-testid={`timeline-node-${step.key}`}
            className="relative flex flex-wrap gap-x-2 sm:gap-x-4 scroll-mt-4 pb-8"
            {...nodeProps}
          >
            {/* Connector line (above current circle, below it) — drawn behind the circle.
                Hidden below `sm` while the node is expanded so it doesn't cross the
                full-width body (F147). */}
            {!isLast && (
              <div
                aria-hidden="true"
                className={cn(
                  'absolute left-[11px] top-6 sm:left-[13px] sm:top-7 bottom-0 w-0.5',
                  ui === 'completed' ? 'bg-success' : 'bg-border',
                  isExpanded && 'hidden sm:block'
                )}
              />
            )}

            {/* Circle */}
            <div
              className={cn(
                'relative z-10 mt-0.5 flex h-6 w-6 sm:h-7 sm:w-7 shrink-0 items-center justify-center rounded-full border text-xs font-medium',
                ui === 'completed' && 'border-transparent bg-success text-primary-foreground',
                ui === 'current' && 'border-transparent bg-foreground text-background',
                ui === 'locked' && 'border-border bg-background text-muted-foreground',
                ui === 'stale' && 'border-warning bg-background text-warning'
              )}
            >
              {ui === 'completed' ? (
                <Check className="h-4 w-4" data-testid={`timeline-check-${step.key}`} />
              ) : ui === 'stale' ? (
                <AlertTriangle className="h-4 w-4" data-testid={`timeline-stale-${step.key}`} />
              ) : (
                <span data-testid={`timeline-number-${step.key}`}>{idx + 1}</span>
              )}
            </div>

            {/* Header row (circle's title + edit affordances) — stays indented */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3
                    className={cn(
                      'text-base font-medium',
                      ui === 'locked' && 'text-muted-foreground'
                    )}
                  >
                    {t(step.labelKey)}
                  </h3>
                  {summary && (
                    <p className="text-sm text-muted-foreground truncate mt-0.5">{summary}</p>
                  )}
                </div>

                {isEditing && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="shrink-0 min-h-11"
                    onClick={() => setEditingStep(null)}
                  >
                    {t('common.cancel')}
                  </Button>
                )}
                {!isExpanded && ui === 'completed' && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="shrink-0 min-h-11"
                    onClick={() => handleEdit(step.key)}
                  >
                    {t('bot.timeline.edit')}
                  </Button>
                )}
                {!isExpanded && ui === 'stale' && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0 min-h-11"
                    onClick={() => handleEdit(step.key)}
                  >
                    {t('bot.timeline.redo')}
                  </Button>
                )}
              </div>
            </div>

            {/* Expanded body — breaks to full width below `sm` (F147); aligns under
                the header text at `sm`+ (28px circle + 16px gap = pl-11). min-w-0
                resets the flex item's automatic minimum size so a content-dense
                section (e.g. the KB catalog, F149) can't push its min-content
                width onto the page as horizontal overflow. */}
            {isExpanded && (
              <div
                className="basis-full min-w-0 pl-0 sm:pl-11 mt-4 transition-all duration-200"
                data-testid={`timeline-body-${step.key}`}
              >
                {step.render({ editing: isEditing, onEditDone: () => setEditingStep(null) })}
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}

function buildSummary(key: SectionKey, profile: TenantProfile | null): string | null {
  if (!profile) return null;
  switch (key) {
    case 'info': {
      const parts: string[] = [];
      if (profile.business_name) parts.push(profile.business_name);
      if (profile.language) parts.push(profile.language);
      return parts.length ? parts.join(' · ') : null;
    }
    default:
      return null;
  }
}
